import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Card,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { getCurrentTOS, updateTOS, listVersions } from 'services/tosService';

const validationSchema = Yup.object({
  version: Yup.string().required('Version number is required'),
  effective_date: Yup.string(),
  content: Yup.string().required('TOS content is required'),
});

function TOSManage() {
  const [currentTOS, setCurrentTOS] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tosData, versionData] = await Promise.all([
        getCurrentTOS().catch(() => null),
        listVersions().catch(() => []),
      ]);
      setCurrentTOS(tosData?.message || tosData);
      setVersions(Array.isArray(versionData?.message || versionData) ? (versionData?.message || versionData) : []);
    } catch (err) {
      console.error('Error loading TOS data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      version: '',
      effective_date: '',
      content: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setUpdating(true);
        await updateTOS({
          ...values,
          required_for_roles: ['TenantAdmin', 'Dashboard', 'Admin'],
        });
        setSnackbar({ open: true, message: 'TOS updated successfully!', severity: 'success' });
        formik.resetForm();
        await loadData();
      } catch (err) {
        console.error('Error updating TOS:', err);
        setSnackbar({ open: true, message: 'Failed to update TOS', severity: 'error' });
      } finally {
        setUpdating(false);
      }
    },
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const isDeprecated = (version) => {
    return !!version.deprecated_date;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* Current TOS Display */}
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" mb={2}>Current TOS Version</SoftTypography>
              {loading ? (
                <SoftBox display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </SoftBox>
              ) : currentTOS ? (
                <>
                  <SoftTypography variant="body2"><strong>Version:</strong> {currentTOS.version}</SoftTypography>
                  <SoftTypography variant="body2"><strong>Effective Date:</strong> {formatDate(currentTOS.effective_date)}</SoftTypography>
                  <SoftTypography variant="body2" mb={2}><strong>Created By:</strong> {currentTOS.created_by || 'N/A'}</SoftTypography>
                  <Divider />
                  <SoftBox
                    mt={2}
                    p={2}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#f8f9fa',
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}
                    dangerouslySetInnerHTML={{ __html: currentTOS.content }}
                  />
                </>
              ) : (
                <Alert severity="warning">No TOS version has been configured yet.</Alert>
              )}
            </SoftBox>
          </Card>
        </SoftBox>

        {/* Create New TOS Version */}
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" mb={2}>Create New TOS Version</SoftTypography>
              <form onSubmit={formik.handleSubmit}>
                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Version Number *</SoftTypography>
                  <SoftInput
                    placeholder="e.g., 1.1, 2.0"
                    name="version"
                    value={formik.values.version}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.version && Boolean(formik.errors.version)}
                  />
                  <SoftTypography variant="caption" color="text">Semantic version number for the TOS</SoftTypography>
                  {formik.touched.version && formik.errors.version && (
                    <SoftBox><SoftTypography variant="caption" color="error">{formik.errors.version}</SoftTypography></SoftBox>
                  )}
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Effective Date</SoftTypography>
                  <SoftInput
                    type="date"
                    name="effective_date"
                    value={formik.values.effective_date}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">TOS Content (HTML) *</SoftTypography>
                  <textarea
                    name="content"
                    value={formik.values.content}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    rows={12}
                    placeholder={'<h1>Terms of Service</h1>\n<p>Enter your TOS content here...</p>'}
                    style={{
                      width: '100%',
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      padding: '12px',
                      borderRadius: '8px',
                      border: formik.touched.content && formik.errors.content
                        ? '1px solid #f44336'
                        : '1px solid #d2d6da',
                      resize: 'vertical',
                    }}
                  />
                  <SoftTypography variant="caption" color="text">HTML content for the Terms of Service</SoftTypography>
                  {formik.touched.content && formik.errors.content && (
                    <SoftBox><SoftTypography variant="caption" color="error">{formik.errors.content}</SoftTypography></SoftBox>
                  )}
                </SoftBox>

                <SoftBox display="flex" gap={2} mb={2}>
                  <SoftButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    disabled={updating || !formik.isValid}
                  >
                    {updating ? 'Updating...' : 'Update TOS'}
                  </SoftButton>
                  <SoftButton
                    variant="gradient"
                    color="secondary"
                    onClick={() => formik.resetForm()}
                    disabled={updating}
                  >
                    Reset
                  </SoftButton>
                </SoftBox>

                <Alert severity="info">
                  <strong>Note:</strong> Updating the TOS will require all users to accept the new version on their next login.
                </Alert>
              </form>
            </SoftBox>
          </Card>
        </SoftBox>

        {/* Version History */}
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" mb={2}>Version History</SoftTypography>
              {versions.length === 0 ? (
                <Alert severity="info">No TOS versions found.</Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        <TableCell>Version</TableCell>
                        <TableCell>Effective Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Deprecated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {versions.map((version, index) => (
                        <TableRow
                          key={version.version || index}
                          sx={isDeprecated(version) ? { backgroundColor: '#f5f5f5' } : {}}
                        >
                          <TableCell>
                            <SoftTypography variant="caption" fontWeight="bold">
                              {version.version}
                            </SoftTypography>
                            {!isDeprecated(version) && (
                              <Chip label="Current" size="small" color="success" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {formatDate(version.effective_date)}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isDeprecated(version) ? 'Deprecated' : 'Active'}
                              size="small"
                              color={isDeprecated(version) ? 'default' : 'success'}
                            />
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {version.created_by || 'N/A'}
                            </SoftTypography>
                          </TableCell>
                          <TableCell>
                            <SoftTypography variant="caption">
                              {isDeprecated(version) ? formatDate(version.deprecated_date) : '-'}
                            </SoftTypography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default TOSManage;
